"use client"

import React, { Component } from "react"
import axios from "axios"
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Switch,
  IconButton,
  Paper,
  Grid,
  Select as MuiSelect,
  MenuItem,
  InputLabel,
} from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import DragIndicatorIcon from "@mui/icons-material/DragIndicator"
import AddIcon from "@mui/icons-material/Add"
import API, { URL_PATH } from "../../src/services/API"


// Field type options
const fieldTypes = ["Checkbox", "Dropdown", "Radio"]
const titleFormats = ["Label", "Heading", "Hide"]

// Map UI field types to API values
const fieldTypeToApiValue = {
  "Checkbox": "checkbox",
  "Dropdown": "dropdown",
  "Radio": "radio"
}

// Map UI title formats to API values
const titleFormatToApiValue = {
  "Label": "label",
  "Heading": "heading",
  "Hide": "hide"
}




class AddOnsTab extends Component {
  constructor(props) {
    super(props)
    this.state = {
      addons: [],
      showForm: false,
      currentAddon: null,
      editingAddonId: null,
      newOption: {
        name: "",
        price: "0",
      },
      isLoading: false,
      error: null,
    }
  }

  // Initialize a new add-on
  initializeNewAddon = () => {
    this.setState({
      currentAddon: {
        id: Date.now().toString(),
        name: "",
        fieldType: "Checkbox",
        title: "",
        titleFormat: "Label",
        required: false,
        description: false,
        descriptionText: "",
        options: [],
      },
      showForm: true,
      editingAddonId: null,
    })
  }

  // Edit an existing add-on
  editAddon = (id) => {
    const addon = this.state.addons.find((addon) => addon.id === id)
    if (addon) {
      this.setState({
        currentAddon: { ...addon },
        showForm: true,
        editingAddonId: id,
      })
    }
  }

  // Handle field changes for the current add-on
  handleAddonChange = (field, value) => {
    this.setState((prevState) => ({
      currentAddon: {
        ...prevState.currentAddon,
        [field]: value,
      },
    }))
  }

  // Add a new option to the current add-on
  addOption = () => {
    const { newOption, currentAddon } = this.state

    if (!newOption.name.trim()) {
      return
    }

    const newOptionObj = {
      id: Date.now().toString(),
      name: newOption.name,
      price: newOption.price || "0",
      isDefault: false,
    }

    this.setState((prevState) => ({
      currentAddon: {
        ...prevState.currentAddon,
        options: [...prevState.currentAddon.options, newOptionObj],
      },
      newOption: {
        name: "",
        price: "0",
      },
    }))
  }

  // Remove an option from the current add-on
  removeOption = (id) => {
    this.setState((prevState) => ({
      currentAddon: {
        ...prevState.currentAddon,
        options: prevState.currentAddon.options.filter((option) => option.id !== id),
      },
    }))
  }

  // Update an option in the current add-on
  updateOption = (id, field, value) => {
    this.setState((prevState) => ({
      currentAddon: {
        ...prevState.currentAddon,
        options: prevState.currentAddon.options.map((option) =>
          option.id === id ? { ...option, [field]: value } : option,
        ),
      },
    }))
  }

  // Format the add-on addon_field for the API
  formatAddonForAPI = (addon) => {
    return {
      hotel_id: this.props.hotelId,
      hotel_menu_id: this.props.menuId,
      name: addon.name,
      input_type: fieldTypeToApiValue[addon.fieldType] || "checkbox",
      title_format: titleFormatToApiValue[addon.titleFormat] || "label",
      is_required: addon.required,
      description: addon.description ? addon.descriptionText || addon.name : "",
      options: addon.options.map((option) => ({
        name: option.name,
        price: Number.parseFloat(option.price) || 0,
        is_default_selected: option.isDefault,
      })),
    }
  }

  // Save the current add-on
 // In the AddOnsTab component, modify the saveAddon method:
saveAddon = async () => {
  const { currentAddon, editingAddonId, addons } = this.state

  if (!currentAddon.name.trim() || !currentAddon.title.trim()) {
    if (this.props.openModal) {
      this.props.openModal("error", "Error", "Name and title are required fields")
    }
    return
  }

  if (currentAddon.options.length === 0) {
    if (this.props.openModal) {
      this.props.openModal("error", "Error", "At least one option is required")
    }
    return
  }

  this.setState({ isLoading: true, error: null })

  try {
    const formattedData = this.formatAddonForAPI(currentAddon)
    console.log("Sending add-on addon_field:", formattedData)

    const endpoint = editingAddonId 
      ? `${URL_PATH.updateaddon}${editingAddonId}`
      : URL_PATH.addaddon;

    const { message, addon_field } = await API(
      this,
      "post",
      endpoint,
      formattedData
    )

    const status = "success" // Temporary - replace with actual status check from API

    if (status === "success") {
      // Update local state
      if (editingAddonId) {
        this.setState({
          addons: addons.map((addon) => (addon.id === editingAddonId ? currentAddon : addon)),
          showForm: false,
          currentAddon: null,
          editingAddonId: null,
        })
      } else {
        const newAddon = { ...currentAddon }
        if (addon_field && addon_field.id) {
          newAddon.id = addon_field.id
        }

        this.setState({
          addons: [...addons, newAddon],
          showForm: false,
          currentAddon: null,
        })
      }

      // Show success message
      if (this.props.openModal) {
        this.props.openModal("success", "Success", message || "Add-on saved successfully")
      }

      // Add this line to navigate to Nutrition tab after saving
      if (this.props.onSuccess) {
        this.props.onSuccess()
      }
    } else {
      if (this.props.openModal) {
        this.props.openModal("error", "Error", message || "Failed to save add-on")
      }
      this.setState({ error: message || "Failed to save add-on" })
    }
  } catch (error) {
    console.error("Error saving add-on:", error)
    let errorMessage = "An error occurred while saving the add-on"
    if (error.response) {
      errorMessage = error.response.addon_field?.message || errorMessage
    }
    if (this.props.openModal) {
      this.props.openModal("error", "Error", errorMessage)
    }
    this.setState({ error: errorMessage })
  } finally {
    this.setState({ isLoading: false })
  }
}

  // Delete an add-on
  deleteAddon = async (id) => {

    try {
      this.setState({ isLoading: true })

      // Make API call to delete add-on
      const { status, message } = await API(this, "post", URL_PATH.deleteAddon, {
        hotel_id: this.props.hotelId,
        hotel_menu_id: this.props.menuId,
        addon_id: id,
      })

      if (status === "success") {
        this.setState({
          addons: this.state.addons.filter((addon) => addon.id !== id),
        })

        if (this.props.openModal) {
          this.props.openModal("success", "Success", message || "Add-on deleted successfully")
        }
      } else {
        if (this.props.openModal) {
          this.props.openModal("error", "Error", message || "Failed to delete add-on")
        }
      }
    } catch (error) {
      console.error("Error deleting add-on:", error)

      if (this.props.openModal) {
        this.props.openModal("error", "Error", "An error occurred while deleting the add-on")
      }
    } finally {
      this.setState({ isLoading: false })
    }
  }

  // Handle option field changes
  handleOptionChange = (field, value) => {
    this.setState((prevState) => ({
      newOption: {
        ...prevState.newOption,
        [field]: value,
      },
    }))
  }

  // Load add-ons from API
  loadAddons = async () => {
    if (!this.props.hotelId || !this.props.menuId) {
      return
    }

    try {
      this.setState({ isLoading: true })

      const payload = {
        hotel_menu_id: this.props.menuId,
        hotel_id: this.props.hotelId,
      }

      const response = await API(this, "post", URL_PATH.getAddons, payload)

      if (response && Array.isArray(response)) {
        // Transform API response to component format
        const formattedAddons = response.map((addon) => ({
          id: addon.id,
          name: addon.name,
          fieldType: this.getFieldTypeFromApi(addon.input_type),
          title: addon.name,
          titleFormat: this.getTitleFormatFromApi(addon.title_format),
          required: addon.is_required === 1,
          description: !!addon.description,
          descriptionText: addon.description || "",
          options: Array.isArray(addon.options)
            ? addon.options.map((opt) => ({
              id: opt.id || Date.now().toString() + Math.random(),
              name: opt.name,
              price: opt.price.toString(),
              isDefault: opt.is_default_selected === 1,
            }))
            : [],
        }))

        this.setState({ addons: formattedAddons })
      }
    } catch (error) {
      console.error("Error loading add-ons:", error)
      if (this.props.openModal) {
        this.props.openModal("error", "Error", "Failed to load add-ons")
      }
    } finally {
      this.setState({ isLoading: false })
    }
  }

  // Helper to convert API field type to UI field type
  getFieldTypeFromApi = (apiType) => {
    const mapping = {
      checkbox: "Checkbox",
      dropdown: "Dropdown",
      radio: "Radio",
    }
    return mapping[apiType] || "Checkbox"
  }

  // Helper to convert API title format to UI title format
  getTitleFormatFromApi = (apiFormat) => {
    const mapping = {
      label: "Label",
      heading: "Heading",
      hide: "Hide",
    }
    return mapping[apiFormat] || "Label"
  }

  componentDidMount() {
    this.loadAddons()
  }

  componentDidUpdate(prevProps) {
    // Reload add-ons if menuId changes
    if (prevProps.menuId !== this.props.menuId && this.props.menuId) {
      this.loadAddons()
    }
  }

  render() {
    const { addons, showForm, currentAddon, newOption, isLoading } = this.state

    return (
      <Box sx={{ width: "100%" }}>
        <Paper
          sx={{
            bgcolor: "#02111B",
            backgroundImage: "none",
            overflow: "hidden",
            mb: 4,
          }}
        >
          <Box sx={{ p: 3 }}>


            {/* List of existing add-ons */}
            <Box sx={{ mb: 3 }}>
              {addons.map((addon) => (
                <Box
                  key={addon.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 2,
                    mb: 1,
                    bgcolor: "#041A29",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                  onClick={() => this.editAddon(addon.id)}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <DragIndicatorIcon sx={{ color: "rgba(255, 255, 255, 0.5)", mr: 1 }} />
                    <Typography color="text.primary">{addon.name}</Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      this.deleteAddon(addon.id)
                    }}
                  >
                    <DeleteIcon sx={{ color: "#BB0A21" }} />
                  </IconButton>
                </Box>
              ))}
            </Box>

            {/* Add new field button */}
            {!showForm && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                <Button
                  variant="contained"
                  onClick={this.initializeNewAddon}
                  sx={{
                    px: 3,
                    py: 1,
                    borderRadius: 1,
                    textTransform: "uppercase",
                    fontWeight: 700,
                    bgcolor: "#FFC857",
                    "&:hover": { bgcolor: "#E6B54E" },
                  }}
                >
                  + ADD NEW FIELD
                </Button>

              </Box>
            )}

            {/* Add/Edit add-on form */}
            {showForm && currentAddon && (
              <Box sx={{ mt: 3 }}>
              

                <Box sx={{ p: 3 }}>
                  {/* Field Type */}
                  <Box sx={{ mb: 3 }}>
                    <FormControl fullWidth>
                      <InputLabel
                        id="field-type-label"
                        sx={{ color: "#fff" }}
                      >
                        Type
                      </InputLabel>
                      <MuiSelect
                        labelId="field-type-label"
                        id="field-type"
                        value={currentAddon.fieldType}
                        label="Type"
                        onChange={(e) => this.handleAddonChange("fieldType", e.target.value)}
                        sx={{
                          bgcolor: "#02111B",
                          color: "#fff",
                          ".MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(255, 255, 255, 0.23)",
                          },
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              bgcolor: "#02111B",
                              color: "#fff",
                            },
                          },
                        }}
                      >
                        {fieldTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </MuiSelect>
                    </FormControl>
                  </Box>


                  {/* Title */}
                  <Box sx={{ mb: 3 }}>
                    <TextField
                      fullWidth
                      label="Title"
                      value={currentAddon.title}
                      onChange={(e) => {
                        this.handleAddonChange("title", e.target.value);
                        this.handleAddonChange("name", e.target.value);
                      }}
                      placeholder="Enter title"
                      sx={{
                        bgcolor: "#02111B",
                        input: { color: "#fff" },
                        label: { color: "#fff" },
                        ".MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(255, 255, 255, 0.23)",
                        },
                      }}
                    />
                  </Box>



                  {/* Title Format */}
                  <Box sx={{ mb: 3 }}>
                    <FormControl fullWidth>
                      <InputLabel
                        id="title-format-label"
                        sx={{ color: "#fff" }}
                      >
                        Title Format
                      </InputLabel>
                      <MuiSelect
                        labelId="title-format-label"
                        id="title-format"
                        value={currentAddon.titleFormat}
                        label="Title Format"
                        onChange={(e) => this.handleAddonChange("titleFormat", e.target.value)}
                        sx={{
                          bgcolor: "#02111B",
                          color: "#fff",
                          ".MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(255, 255, 255, 0.23)",
                          },
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              bgcolor: "#02111B",
                              color: "#fff",
                            },
                          },
                        }}
                      >
                        {titleFormats.map((format) => (
                          <MenuItem key={format} value={format}>
                            {format}
                          </MenuItem>
                        ))}
                      </MuiSelect>
                    </FormControl>
                  </Box>

                  {/* Options */}
                  <Box sx={{ mb: 3 }}>
                    {currentAddon.options.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        {/* Header Row */}
                        <Grid container sx={{ mb: 1, px: 1 }}>
                          <Grid item xs={5}>
                            <Typography variant="caption" color="text.secondary">

                            </Typography>
                          </Grid>
                          <Grid item xs={5}>
                            <Typography variant="caption" color="text.secondary">

                            </Typography>
                          </Grid>
                          <Grid item xs={1} sx={{ textAlign: "center" }}>
                            <Typography variant="caption" color="text.secondary">
                              Default
                            </Typography>
                          </Grid>
                          <Grid item xs={1}></Grid>
                        </Grid>

                        {/* Options List */}
                        {currentAddon.options.map((option) => (
                          <Box key={option.id} sx={{ mb: 1 }}>
                            <Grid container spacing={1} alignItems="stretch">
                              <Grid item xs={5}>
                                <TextField
                                  fullWidth
                                  label="Name"
                                  value={option.name}
                                  onChange={(e) => this.updateOption(option.id, "name", e.target.value)}
                                  sx={{
                                    bgcolor: "#02111B",
                                    input: { color: "#fff" },
                                    label: { color: "#fff" },
                                    ".MuiOutlinedInput-notchedOutline": {
                                      borderColor: "rgba(255, 255, 255, 0.23)",
                                    },
                                  }}
                                />
                              </Grid>
                              <Grid item xs={5}>
                                <TextField
                                  fullWidth
                                  type="number"
                                  label="Price"
                                  value={option.price}
                                  onChange={(e) => this.updateOption(option.id, "price", e.target.value)}
                                  sx={{
                                    bgcolor: "#02111B",
                                    input: { color: "#fff" },
                                    label: { color: "#fff" },
                                    ".MuiOutlinedInput-notchedOutline": {
                                      borderColor: "rgba(255, 255, 255, 0.23)",
                                    },
                                  }}
                                />
                              </Grid>
                              <Grid item xs={1}>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                                  <FormControlLabel
                                    control={
                                      <Radio
                                        checked={option.isDefault}
                                        onChange={(e) => {
                                          currentAddon.options.forEach((opt) => {
                                            if (opt.id !== option.id) {
                                              this.updateOption(opt.id, "isDefault", false);
                                            }
                                          });
                                          this.updateOption(option.id, "isDefault", e.target.checked);
                                        }}
                                        sx={{
                                          color: "#FFC857",
                                          "&.Mui-checked": {
                                            color: "#FFC857",
                                          },
                                        }}
                                      />
                                    }
                                    label=""
                                  />
                                </Box>
                              </Grid>
                              <Grid item xs={1}>
                                <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                                  <IconButton onClick={() => this.removeOption(option.id)}>
                                    <DeleteIcon sx={{ color: "#BB0A21" }} />
                                  </IconButton>
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Add Option Form */}
                    <Box sx={{ mb: 2 }}>
                      <Grid container spacing={1} alignItems="stretch">
                        <Grid item xs={5}>
                          <TextField
                            fullWidth
                            label="Option Name"
                            value={newOption.name}
                            onChange={(e) => this.handleOptionChange("name", e.target.value)}
                            sx={{
                              height: "100%",
                              bgcolor: "#02111B",
                              input: { color: "#fff" },
                              label: { color: "#fff" },
                              ".MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(255, 255, 255, 0.23)",
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={5}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Price"
                            value={newOption.price}
                            onChange={(e) => this.handleOptionChange("price", e.target.value)}
                            sx={{
                              height: "100%",
                              bgcolor: "#02111B",
                              input: { color: "#fff" },
                              label: { color: "#fff" },
                              ".MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(255, 255, 255, 0.23)",
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <Box sx={{ height: "100%" }}>
                            <Button
                              fullWidth
                              variant="outlined"
                              onClick={this.addOption}
                              sx={{
                                height: "100%",
                                borderColor: "#FFC857",
                                color: "black",
                                backgroundColor: "#FFC857",
                                fontWeight: 700,
                                "&:hover": {
                                  borderColor: "#E6B54E",
                                  backgroundColor: "#FFC857",
                                },
                              }}
                            >
                              Add Option
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>


                  {/* Required */}

                  <div>
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box>
                          <Typography variant="body2" color="text.primary">
                            Required
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Is this product addons required?
                          </Typography>
                        </Box>
                        <Switch
                          checked={currentAddon.required}
                          onChange={(e) => this.handleAddonChange("required", e.target.checked)}
                          // sx={{
                          //   "& .MuiSwitch-switchBase.Mui-checked": {
                          //     color: "#FFC857",
                          //     "&:hover": {
                          //       backgroundColor: "rgba(255, 200, 87, 0.08)",
                          //     },
                          //   },
                          //   "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                          //     backgroundColor: "#FFC857",
                          //   },
                          // }}
                        />
                      </Box>
                    </Box>

                    {/* Description */}
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box>
                          <Typography variant="body2" color="text.primary">
                            Description
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Add product addons description
                          </Typography>
                        </Box>
                        <Switch
                          checked={currentAddon.description}
                          onChange={(e) => this.handleAddonChange("description", e.target.checked)}
                          // sx={{
                          //   "& .MuiSwitch-switchBase.Mui-checked": {
                          //     color: "#FFC857",
                          //     "&:hover": {
                          //       backgroundColor: "rgba(255, 200, 87, 0.08)",
                          //     },
                          //   },
                          //   "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                          //     backgroundColor: "#FFC857",
                          //   },
                          // }}
                        />
                      </Box>
                    </Box>
                  </div>


                  {/* Description Text - Only show if description is enabled */}
                  {currentAddon.description && (
                  <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Description Text"  // 👈 this acts like placeholder + floating label
                    value={currentAddon.descriptionText}
                    onChange={(e) => this.handleAddonChange("descriptionText", e.target.value)}
                    sx={{
                      bgcolor: "#02111B",
                      input: { color: "#fff" },
                      label: { color: "#fff" },
                      ".MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255, 255, 255, 0.23)",
                      },
                      "& .MuiInputBase-input": {
                        color: "#fff",
                      },
                    }}
                  />
                </Box>
                
                  )}



                  {/* Save button */}
                  <Box sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      onClick={this.saveAddon}
                      disabled={isLoading}
                      sx={{
                        px: 4,
                        py: 1,
                        borderRadius: 1,
                        textTransform: "none",
                        fontWeight: 700,
                        bgcolor: "#FFC857",
                        "&:hover": { bgcolor: "#E6B54E" },
                      }}
                    >
                     
                      {isLoading ? "SAVING..." : this.state.editingAddonId ? "UPDATE ADD-ON" : "SAVE & NEXT"}

                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    )
  }
}


export default AddOnsTab